/* Boost owns matching/ranking; Liquid owns product offers and market prices. */
(() => {
  'use strict';
  const API = 'https://services.mybcapps.com/bc-sf-filter/';
  const creatorCollections = collections => (collections || []).filter(item =>
    item.template_suffix === 'brand' && /^[a-z0-9][a-z0-9-]*$/.test(item.handle)).slice(0, 3);
  const node = (tag, text, className) => {
    const el = document.createElement(tag);
    if (text != null) el.textContent = text;
    if (className) el.className = className;
    return el;
  };
  class Search {
    constructor(panel) {
      this.panel = panel;
      this.header = panel.closest('[data-site-header]');
      this.input = this.header.querySelector('[data-live-search-input]');
      this.form = this.input.form;
      this.select = this.form.querySelector('[data-og-purchase-type-select]');
      this.mobile = panel.querySelector('[data-og-search-mobile]');
      this.content = panel.querySelector('[data-og-search-content]');
      this.status = panel.querySelector('[data-og-search-status]');
      this.all = panel.querySelector('[data-og-search-all]');
      this.retry = panel.querySelector('[data-og-search-retry]');
      this.backdrop = panel.previousElementSibling;
      this.media = window.matchMedia('(max-width: 719px)');
      this.events = new AbortController();
      this.sequence = 0;
      this.cache = new Map();
      this.bannerCache = new Map();
      this.inertElements = new Map();
      document.body.append(this.backdrop, panel);
      this.input.setAttribute('aria-controls', panel.id);
      this.input.setAttribute('aria-expanded', 'false');
      const listen = (el, type, fn, capture = false) => el?.addEventListener(type, fn, { signal: this.events.signal, capture });
      if (this.select) {
        this.mobileSelect = node('select');
        for (const option of this.select.options) this.mobileSelect.append(option.cloneNode(true));
        this.mobileSelect.selectedIndex = this.select.selectedIndex;
        const label = panel.querySelector('[data-og-search-type]');
        label.append(this.mobileSelect);
        label.hidden = false;
        listen(this.mobileSelect, 'change', () => {
          this.select.selectedIndex = this.mobileSelect.selectedIndex;
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      for (const input of [this.input, this.mobile]) {
        listen(input, 'input', () => {
          this.input.value = this.mobile.value = input.value;
          this.schedule();
        });
      }
      listen(this.input, 'focus', () => this.open());
      listen(this.input, 'click', () => this.open());
      listen(this.form, 'submit', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.input.value.trim()) window.location.assign(this.searchUrl());
      }, true);
      listen(this.select, 'change', () => {
        if (this.mobileSelect) this.mobileSelect.selectedIndex = this.select.selectedIndex;
        if (!panel.hidden) this.schedule();
      });
      listen(this.header.querySelector('[data-mobile-search-button]'), 'click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.opener = event.currentTarget;
        this.open();
      }, true);
      panel.querySelectorAll('[data-og-search-close]').forEach(button => listen(button, 'click', () => this.close()));
      listen(this.backdrop, 'click', () => this.close());
      listen(panel.querySelector('[data-og-search-clear]'), 'click', () => {
        this.input.value = this.mobile.value = '';
        this.schedule();
        this.mobile.focus();
      });
      listen(this.retry, 'click', () => this.schedule());
      listen(document, 'keydown', event => this.keydown(event));
      listen(document, 'pointerdown', event => {
        if (!panel.hidden && !this.media.matches && !panel.contains(event.target) && !this.form.contains(event.target)) this.close(false);
      });
      listen(document, 'focusin', event => {
        if (!panel.hidden && !this.media.matches && !panel.contains(event.target) && !this.form.contains(event.target)) this.close(false);
      });
      listen(window, 'resize', () => { if (!panel.hidden) { this.position(); this.fitProducts(); } });
      listen(window.visualViewport, 'resize', () => { if (!panel.hidden) this.position(); });
      listen(window, 'scroll', () => { if (!panel.hidden) this.position(); }, true);
      listen(document, 'cart:updated', () => this.cache.clear());
      this.media.addEventListener('change', () => {
        if (!panel.hidden) {
          this.modal();
          (this.media.matches ? this.mobile : this.input).focus();
          this.position();
          this.arrangeGroups();
          this.fitProducts();
        }
      }, { signal: this.events.signal });
    }
    searchUrl() {
      const url = new URL(this.panel.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', this.input.value.trim());
      const type = this.select?.selectedOptions[0]?.dataset.ogPurchaseType;
      if (type) url.searchParams.set(this.select.dataset.ogPurchaseFilterParam, type);
      return url.href;
    }
    position() {
      const rect = this.form.getBoundingClientRect();
      const headerRect = this.header.getBoundingClientRect();
      const navigationRect = this.header.querySelector('[data-site-navigation]')?.getBoundingClientRect();
      const main = this.header.querySelector('[data-site-header-main]') || this.header;
      const bounds = main.getBoundingClientRect();
      const inset = Math.max(16, bounds.left + parseFloat(getComputedStyle(main).paddingLeft || 0));
      const viewport = window.visualViewport;
      const bottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight);
      const preview = document.querySelector('#PBarNextFrame')?.getBoundingClientRect();
      // Shopify's preview iframe can cover the whole viewport even though its
      // visible toolbar occupies only the bottom strip.
      const reserved = preview && preview.height > 0 && preview.bottom >= bottom - 2
        ? (preview.height < 120 ? preview.height : 80) : 0;
      // The header (including desktop navigation) stacks above this panel.
      // Never move the panel upward to manufacture extra viewport space.
      const top = Math.max(0, rect.bottom, headerRect.bottom, navigationRect?.bottom || 0) + 8;
      this.panel.style.setProperty('--og-search-mobile-top', `${viewport?.offsetTop || 0}px`);
      this.panel.style.setProperty('--og-search-mobile-height', `${Math.max(0, (viewport?.height || window.innerHeight) - reserved)}px`);
      for (const el of [this.panel, this.backdrop]) {
        el.style.setProperty('--og-search-top', `${top}px`);
        el.style.setProperty('--og-search-height', `${Math.max(0, bottom - reserved - top - 12)}px`);
        el.style.setProperty('--og-search-left', `${inset}px`);
        el.style.setProperty('--og-search-right', `${Math.max(16, window.innerWidth - bounds.right + parseFloat(getComputedStyle(main).paddingRight || 0))}px`);
      }
    }
    modal() {
      const mobile = this.media.matches;
      this.panel.setAttribute('role', mobile ? 'dialog' : 'region');
      if (mobile) {
        this.input.setAttribute('aria-haspopup', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        for (const el of document.body.children) {
          if (el === this.panel || el === this.backdrop || this.inertElements.has(el)) continue;
          this.inertElements.set(el, el.inert);
          el.inert = true;
        }
      } else {
        this.input.removeAttribute('aria-haspopup');
        this.panel.removeAttribute('aria-modal');
        this.restoreInert();
      }
    }
    restoreInert() {
      this.inertElements.forEach((value, el) => { el.inert = value; });
      this.inertElements.clear();
    }
    open() {
      if (!this.panel.hidden || this.restoringFocus) return;
      this.opener ||= this.input;
      this.panel.hidden = this.backdrop.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.mobile.value = this.input.value;
      document.body.classList.add('og-search-open');
      this.position();
      this.modal();
      if (this.media.matches) this.mobile.focus();
      this.schedule();
    }
    close(focus = true) {
      if (this.panel.hidden) return;
      ++this.sequence;
      clearTimeout(this.timer);
      this.request?.abort();
      this.panel.hidden = this.backdrop.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('og-search-open');
      this.restoreInert();
      if (focus) {
        // Focusing the header input must not immediately reopen the panel.
        const target = this.opener || this.input;
        this.restoringFocus = true;
        target.focus();
        this.restoringFocus = false;
      }
      this.opener = null;
    }
    keydown(event) {
      if (this.panel.hidden) return;
      const active = document.activeElement;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key === 'Enter' && active === this.mobile) {
        event.preventDefault();
        if (this.input.value.trim()) window.location.assign(this.searchUrl());
      }
      const focusable = [...this.panel.querySelectorAll('a[href],button,input,select')].filter(el => !el.disabled && el.getClientRects().length);
      const links = [...this.content.querySelectorAll('a[href]'), this.all].filter(el => el.getClientRects().length);
      const index = links.indexOf(active);
      if (event.key === 'ArrowDown' && (active === this.input || active === this.mobile || index >= 0) && links.length) {
        event.preventDefault();
        links[Math.min(index + 1, links.length - 1)].focus();
      }
      if (event.key === 'ArrowUp' && index >= 0) {
        event.preventDefault();
        (index ? links[index - 1] : this.media.matches ? this.mobile : this.input).focus();
      }
      if (event.key === 'Tab' && this.media.matches && focusable.length) {
        if (event.shiftKey && active === focusable[0]) { event.preventDefault(); focusable.at(-1).focus(); }
        else if (!event.shiftKey && active === focusable.at(-1)) { event.preventDefault(); focusable[0].focus(); }
      }
    }
    schedule() {
      ++this.sequence;
      this.request?.abort();
      clearTimeout(this.timer);
      this.content.replaceChildren();
      this.content.classList.remove('og-search__content--split');
      this.retry.hidden = true;
      this.status.classList.remove('visually-hidden');
      this.all.href = this.searchUrl();
      this.all.textContent = 'View all results';
      const query = this.input.value.trim();
      this.all.parentElement.hidden = !query;
      this.status.textContent = query ? 'Searching…' : 'Start typing to discover products, creators and collections.';
      this.content.setAttribute('aria-busy', String(Boolean(query)));
      if (!query) { this.content.setAttribute('aria-busy', 'false'); return; }
      const placeholders = node('div', null, 'og-search__grid og-search__loading');
      placeholders.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 6; i++) {
        const placeholder = node('div');
        placeholder.append(node('div', null, 'og-search__skeleton placeholder--content-image'));
        placeholder.append(node('div', null, 'og-search__skeleton-text placeholder--content-text'));
        placeholders.append(placeholder);
      }
      this.content.append(placeholders);
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.search(query, sequence), 250);
    }
    async json(url, signal) {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
      const data = await response.json();
      if (data.error) throw new Error('Boost search unavailable');
      return data;
    }
    async search(query, sequence) {
      const request = this.request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      try {
        const type = this.select?.selectedOptions[0]?.dataset.ogPurchaseType;
        const url = new URL(API + (type ? 'search' : 'search/suggest'));
        url.search = new URLSearchParams({ shop: this.panel.dataset.shop, q: query,
          product_limit: '10', collection_limit: '10', page_limit: '3', suggestion_limit: '4',
          re_run_if_typo: 'true', limit: '10', page: '1' });
        if (type) {
          // Instant Search has no documented metafield-filter contract. Use a
          // verified full-search facet rather than returning unfiltered products.
          if (!this.facets) {
            // The search-specific tree omits this store's Purchase Type facet.
            const tree = new URL(API + 'filter');
            tree.search = new URLSearchParams({ shop: this.panel.dataset.shop, collection_scope: '0',
              build_filter_tree: 'true', limit: '1' });
            const metadata = await this.json(tree, request.signal);
            this.facets = metadata.filter?.options || [];
          }
          const facet = this.facets.find(item => /fulfillment|purchase.?type/i.test(`${item.label} ${item.filterOptionId}`));
          const value = facet?.values?.find(item => String(item.key || item.label).toLowerCase() === type.toLowerCase());
          if (!facet || facet.status === 'disabled' || !value || !/^pf_[\w-]+(?:::[\w-]+)*$/.test(facet.filterOptionId)) throw new Error('purchase-type');
          url.searchParams.set(`${facet.filterOptionId}[]`, String(value.key));
        }
        const creatorsUrl = new URL(API + 'search/suggest');
        creatorsUrl.search = new URLSearchParams({ shop: this.panel.dataset.shop, q: query,
          product_limit: '1', collection_limit: '10', suggestion_limit: '4', re_run_if_typo: 'true' });
        // Full search supplies filtered products; Instant Search supplies creator
        // matches for the query independently of the selected product format.
        const [data, creators] = await Promise.all([
          this.json(url, request.signal),
          type ? this.json(creatorsUrl, request.signal).catch(() => ({ collections: [] })) : null
        ]);
        if (creators) {
          data.collections = creators.collections;
          data.suggestions = creators.suggestions;
        }
        if (!Array.isArray(data.products)) throw new Error('Invalid search response');
        const cards = new Array(data.products.length);
        const banners = Promise.all(creatorCollections(data.collections).map(async item => ({
          ...item, bannerUrl: await this.creatorBanner(item, request.signal)
        })));
        let next = 0;
        await Promise.all(Array.from({ length: Math.min(3, cards.length) }, async () => {
          while (next < cards.length) {
            const index = next++;
            cards[index] = await this.card(data.products[index], request.signal);
          }
        }));
        data.collections = await banners;
        if (sequence !== this.sequence || this.panel.hidden) return;
        this.render(data, cards);
      } catch (error) {
        if (sequence !== this.sequence || this.panel.hidden) return;
        this.content.replaceChildren();
        this.status.textContent = error.message === 'purchase-type'
          ? 'Instant results aren’t available for this purchase type. View all results to continue your search.'
          : 'We couldn’t load search results. Please try again or view all results.';
        this.retry.hidden = error.message === 'purchase-type';
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.content.setAttribute('aria-busy', 'false');
      }
    }
    async creatorBanner(item, signal) {
      this.bannerCache ||= new Map();
      if (this.bannerCache.has(item.handle)) return this.bannerCache.get(item.handle);
      try {
        const root = this.panel.dataset.root.replace(/\/$/, '');
        const url = new URL(`${root}/collections/${item.handle}`, window.location.origin);
        url.searchParams.set('section_id', 'only-games-search-creator');
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { signal });
        if (!response.ok) return '';
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const banner = html.querySelector('[data-og-search-creator-banner]')?.getAttribute('data-og-search-creator-banner') || '';
        const resolved = banner ? new URL(banner, window.location.origin) : null;
        const value = resolved?.protocol === 'https:' ? resolved.href : '';
        if (this.bannerCache.size >= 48) this.bannerCache.delete(this.bannerCache.keys().next().value);
        this.bannerCache.set(item.handle, value);
        return value;
      } catch (error) {
        // A missing banner must not hide otherwise valid search results.
        return '';
      }
    }
    async card(product, signal) {
      if (!/^[a-z0-9][a-z0-9-]*$/.test(product.handle)) throw new Error('Invalid product handle');
      if (this.cache.has(product.handle)) return this.cache.get(product.handle).cloneNode(true);
      const root = this.panel.dataset.root.replace(/\/$/, '');
      const url = new URL(`${root}/products/${product.handle}`, window.location.origin);
      url.searchParams.set('section_id', 'only-games-search-card');
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Product details unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('[data-og-search-card]');
      if (!card) throw new Error('Missing search card');
      card.querySelectorAll('script').forEach(el => el.remove());
      if (this.cache.size >= 48) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(product.handle, card.cloneNode(true));
      return card;
    }
    group(title, className, count) {
      const group = node('section', null, 'og-search__group');
      group.classList.add(`og-search__group--${title.toLowerCase()}`);
      const heading = node('h3', title, 'search-flydown__group-title');
      if (count != null) heading.append(node('span', ` (${count.toLocaleString()})`, 'og-search__count'));
      group.append(heading);
      const list = node(className === 'og-search__grid' ? 'div' : 'ul', null, className);
      group.append(list);
      this.content.append(group);
      return list;
    }
    creators(items) {
      const list = this.group('Creators', 'og-search__creators');
      const root = this.panel.dataset.root.replace(/\/$/, '');
      for (const item of items) {
        const link = node('a', null, 'og-search-creator');
        link.href = `${root}/collections/${item.handle}`;
        link.title = item.title;
        const backgroundSrc = item.bannerUrl || item.image?.src;
        if (backgroundSrc) {
          const url = new URL(backgroundSrc, window.location.origin);
          if (url.protocol === 'https:') {
            const background = node('img', null, 'og-search-creator__background');
            background.src = url.href;
            background.alt = '';
            background.loading = 'lazy';
            link.append(background);
            link.classList.toggle('og-search-creator--banner', Boolean(item.bannerUrl));
          }
        }
        if (item.image?.src) {
          const imageUrl = new URL(item.image.src, window.location.origin);
          if (imageUrl.protocol === 'https:' && imageUrl.hostname === 'cdn.shopify.com') {
            const image = node('img', null, 'og-search-creator__logo');
            image.src = imageUrl.href;
            image.alt = '';
            image.loading = 'lazy';
            link.append(image);
          }
        }
        link.append(node('span', item.title, 'og-search-creator__name'));
        const li = node('li');
        li.append(link);
        list.append(li);
      }
      if (!items.length) list.parentElement.append(node('p', 'No matching creators found.', 'og-search__creator-empty'));
      const viewAll = node('a', 'View all creators →', 'og-search__creators-all');
      viewAll.href = this.panel.dataset.collectionsUrl || `${root}/collections`;
      list.parentElement.append(viewAll);
    }
    arrangeGroups() {
      const discovery = this.content.querySelector('.og-search__discovery');
      if (!discovery) return;
      if (this.media.matches) this.content.prepend(discovery);
      else this.content.append(discovery);
    }
    fitProducts() {
      const grid = this.content.querySelector('.og-search__group--products .og-search__grid');
      if (!grid) return;
      const cards = [...grid.querySelectorAll('[data-og-search-card]')];
      // Restore every candidate before measuring auto-fit; hidden cards must
      // not cause columns to collapse when the viewport grows again.
      cards.forEach(card => { card.hidden = false; });
      const columns = getComputedStyle(grid).gridTemplateColumns.split(/\s+/).filter(Boolean).length;
      const limit = this.media.matches ? 6 : Math.max(1, columns) * 2;
      cards.forEach((card, index) => { card.hidden = index >= limit; });
    }
    render(data, cards) {
      this.content.replaceChildren();
      const total = Number(data.total_product) || 0;
      const suggestions = this.group('Suggestions', 'og-search__links');
      for (const term of (data.suggestions || []).filter(item => typeof item === 'string').slice(0, 4)) {
        const link = node('a');
        const url = new URL(this.searchUrl());
        url.searchParams.set('q', term);
        link.href = url.href;
        link.append(node('span', term));
        const item = node('li');
        item.append(link);
        suggestions.append(item);
      }
      if (!suggestions.children.length) suggestions.parentElement.append(node('p', 'No matching suggestions.', 'og-search__creator-empty'));
      if (cards.length) this.group('Products', 'og-search__grid', total).append(...cards);
      this.creators(creatorCollections(data.collections));
      const discovery = node('div', null, 'og-search__discovery');
      for (const group of [...this.content.children]) {
        if (group.classList.contains('og-search__group--creators')) discovery.append(group);
      }
      if (discovery.children.length) this.content.append(discovery);
      this.arrangeGroups();
      this.content.classList.toggle('og-search__content--split', cards.length > 0 && discovery.children.length > 0);
      this.fitProducts();
      this.all.textContent = total ? `View all ${total.toLocaleString()} products →` : 'View all results →';
      this.status.textContent = cards.length ? `${total.toLocaleString()} matching products` : 'No matching products found.';
      this.status.classList.toggle('visually-hidden', cards.length > 0);
      if (data.correct_query) this.status.textContent += ` Showing results for “${data.correct_query}”.`;
    }
    destroy() {
      this.close(false);
      this.events.abort();
      this.panel.remove();
      this.backdrop.remove();
    }
  }
  if (typeof module === 'object' && module.exports && typeof window === 'undefined') {
    module.exports = { Search, creatorCollections };
    return;
  }
  const instances = new Map();
  function initialize(scope = document) {
    scope.querySelectorAll('[data-og-search]').forEach(panel => {
      const header = panel.closest('[data-site-header]');
      if (!header || instances.has(header)) return;
      instances.set(header, new Search(panel));
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [header, instance] of instances) {
      if (!event.target.contains(header)) continue;
      instance.destroy();
      instances.delete(header);
    }
  });
})();
